/** Mirrors the Laravel error envelope: { message, code, errors?, meta? }. */
export interface ApiErrorBody {
  message?: string
  code?: string
  errors?: Record<string, string[]>
  meta?: Record<string, unknown>
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly errors: Record<string, string[]>
  readonly meta: Record<string, unknown>

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? 'Request failed.')
    this.name = 'ApiError'
    this.status = status
    this.code = body.code ?? (status === 0 ? 'network_error' : 'http_error')
    this.errors = body.errors ?? {}
    this.meta = body.meta ?? {}
  }

  get isUnauthenticated() {
    return this.status === 401
  }

  get isValidation() {
    return this.status === 422
  }

  get isPlanLimit() {
    return this.code === 'plan_limit_reached'
  }

  firstFieldError(field: string): string | undefined {
    return this.errors[field]?.[0]
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/** Human-readable message for any thrown value. */
export function errorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message) return error.message
  return fallback
}
